/* BRAND-MOCK: throwaway identity mock for the "glamorous conspiratorial dinner
   party" direction. Everything here is self-contained:
     - all brand tokens are scoped to `.page` in Brand.module.scss (no global
       changes — the rest of the app is untouched)
     - the only external hooks are the Bodoni Moda <link> in index.html and the
       /brand route in App.tsx
   To tear down: delete this folder, remove the route + import in App.tsx, and
   remove the Bodoni link in index.html. Nothing else references it. */
import { useState } from "react";
import clsx from "clsx";
import styles from "./Brand.module.scss";

/* BRAND-MOCK: reuse the existing home photos as stand-in "suspect" portraits so
   the duotone image treatment is shown on real images, not gray boxes */
const homeImages = import.meta.glob<{ default: string }>(
  "../../assets/home/*.{png,jpg,jpeg,webp,gif,svg}",
  { eager: true }
);
const photoSrcs = Object.keys(homeImages)
  .sort((a, b) => a.localeCompare(b))
  .map((key) => homeImages[key]!.default);

/* BRAND-MOCK: the wax seal — the one mark, reused as logo, watermark and spinner.
   `uid` keeps the curved-text path id unique per instance. */
const Seal = ({
  uid,
  showText = true,
  className,
}: {
  uid: string;
  showText?: boolean;
  className?: string;
}) => (
  <svg viewBox="0 0 100 100" className={clsx(styles.seal, className)} aria-hidden focusable="false">
    <defs>
      <path id={`seal-ring-${uid}`} d="M50,50 m-33,0 a33,33 0 1,1 66,0 a33,33 0 1,1 -66,0" fill="none" />
    </defs>
    <circle cx="50" cy="50" r="47" className={styles.sealWax} />
    <circle cx="50" cy="50" r="41.5" className={styles.sealRing} />
    <circle cx="50" cy="50" r="37.5" className={styles.sealHair} />
    {showText && (
      <text className={styles.sealText}>
        <textPath href={`#seal-ring-${uid}`} startOffset="0">
          WHODUNIT · A MURDER MYSTERY DINNER ·
        </textPath>
      </text>
    )}
    <text x="50" y="63" textAnchor="middle" className={styles.sealMonogram}>W</text>
  </svg>
);

const SUSPECTS = [
  { name: "The Heiress", line: "Gracious, generous — and first in the will." },
  { name: "The Maître d’", line: "Sees every table. Forgets nothing." },
  { name: "The Old Flame", line: "Swears the affair ended years ago." },
  { name: "The Partner", line: "Signed the deal. Regrets the terms." },
];

const STEPS = [
  { title: "Choose your case", line: "Pick a mystery that fits your table. We cast the characters." },
  { title: "Send the invitations", line: "Share one link. Each guest gets a secret, a motive, an alibi." },
  { title: "Play out the night", line: "Clues drop course by course. By dessert, someone confesses." },
];

const SWATCHES = [
  { name: "Ink", note: "The room after midnight", cls: styles.swInk },
  { name: "Oxblood", note: "The house color", cls: styles.swOxblood },
  { name: "Brass", note: "Candlelight & cutlery", cls: styles.swBrass },
  { name: "Cream", note: "The place cards", cls: styles.swCream },
];

const BrandMock = () => {
  const [sealing, setSealing] = useState(true);

  return (
    <div className={styles.page}>
      {/* ── Masthead ─────────────────────────────────────────────── */}
      <header className={styles.masthead}>
        <div className={styles.lockup}>
          <Seal uid="logo" className={styles.logoSeal} />
          <div>
            <p className={styles.wordmark}>Whodunit</p>
            <p className={styles.wordmarkSub}>Murder Mystery Dinners</p>
          </div>
        </div>
        <span className={styles.mastNote}>Est. MMXXV · By invitation</span>
      </header>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className={styles.hero}>
        <Seal uid="hero" showText={false} className={styles.heroWatermark} />
        <div className={styles.heroInner}>
          <p className={styles.eyebrow}>A murder mystery dinner party</p>
          <h1 className={styles.heroTitle}>
            Someone at dinner
            <span className={styles.heroTitleEm}> is lying.</span>
          </h1>
          <p className={styles.heroLede}>
            You host the evening. We hand out the secrets, the motives and the
            alibis. By dessert, one of your friends is a murderer — and everyone
            at the table is a suspect.
          </p>
          <div className={styles.heroCtas}>
            <button type="button" className={styles.btnPrimary}>
              Choose your case <span className={styles.arrow} aria-hidden>→</span>
            </button>
            <button type="button" className={styles.btnGhost}>
              See how it works
            </button>
          </div>
        </div>
      </section>

      {/* ── Statement band ───────────────────────────────────────── */}
      <section className={styles.statement}>
        <p className={styles.statementText}>
          You bring the guests.
          <span className={styles.statementEm}> We bring the motive.</span>
        </p>
      </section>

      {/* ── Suspects (duotone image treatment + voice) ───────────── */}
      <section className={styles.suspects}>
        <div className={styles.sectionHead}>
          <p className={styles.eyebrow}>The guest list</p>
          <h2 className={styles.sectionTitle}>Everyone has something to hide.</h2>
        </div>
        <div className={styles.suspectGrid}>
          {SUSPECTS.map((s, i) => (
            <figure key={s.name} className={styles.suspect}>
              <div className={styles.suspectFrame}>
                {photoSrcs[i] ? (
                  <img className={styles.suspectPhoto} src={photoSrcs[i]} alt="" />
                ) : (
                  <div className={styles.suspectPhotoFallback} aria-hidden />
                )}
                <span className={styles.suspectTag}>№ {String(i + 1).padStart(2, "0")}</span>
              </div>
              <figcaption>
                <p className={styles.suspectName}>{s.name}</p>
                <p className={styles.suspectLine}>{s.line}</p>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* ── How it works ─────────────────────────────────────────── */}
      <section className={styles.how}>
        <div className={styles.sectionHead}>
          <p className={styles.eyebrow}>The evening</p>
          <h2 className={styles.sectionTitle}>Three courses. One killer.</h2>
        </div>
        <ol className={styles.steps}>
          {STEPS.map((step, i) => (
            <li key={step.title} className={styles.step}>
              <span className={styles.stepNo}>{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h3 className={styles.stepTitle}>{step.title}</h3>
                <p className={styles.stepLine}>{step.line}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>

      {/* ── House style (the system, laid bare for review) ───────── */}
      <section className={styles.system}>
        <div className={styles.sectionHead}>
          <p className={styles.eyebrow}>Identity</p>
          <h2 className={styles.sectionTitle}>The house style.</h2>
        </div>

        <div className={styles.systemGrid}>
          {/* Palette */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>Palette</p>
            <div className={styles.swatches}>
              {SWATCHES.map((s) => (
                <div key={s.name} className={styles.swatch}>
                  <span className={clsx(styles.swatchChip, s.cls)} />
                  <p className={styles.swatchName}>{s.name}</p>
                  <p className={styles.swatchNote}>{s.note}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Type */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>Type</p>
            <p className={styles.specimenDisplay}>Aa</p>
            <p className={styles.specimenMeta}>Bodoni Moda · display &amp; headlines</p>
            <p className={styles.specimenBody}>Aa — Inter · body &amp; interface</p>
            <p className={styles.specimenTrack}>The guest list</p>
          </div>

          {/* The mark */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>The mark</p>
            <div className={styles.markRow}>
              <Seal uid="sys1" className={styles.markLg} />
              <Seal uid="sys2" showText={false} className={styles.markSm} />
            </div>
            <p className={styles.swatchNote}>One wax seal — logo, watermark, loader.</p>
          </div>

          {/* Micro-craft: cursor + spinner */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>Micro-craft</p>
            <div className={styles.craftMagnifier}>
              Hover here — the cursor turns detective.
            </div>
            <div className={styles.craftSpinnerRow}>
              <Seal uid="spin" showText={false} className={clsx(styles.spinner, sealing && styles.spinnerOn)} />
              <button type="button" className={styles.textToggle} onClick={() => setSealing((v) => !v)}>
                {sealing ? "Sealing the envelopes…" : "Start the seal"}
              </button>
            </div>
          </div>

          {/* Micro-craft: in-character empty state */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>Empty state</p>
            <div className={styles.emptyState}>
              <Seal uid="empty" showText={false} className={styles.emptySeal} />
              <p className={styles.emptyTitle}>No suspects yet.</p>
              <p className={styles.swatchNote}>The guest list is suspiciously short.</p>
            </div>
          </div>

          {/* Micro-craft: 404 in voice */}
          <div className={styles.card}>
            <p className={styles.cardLabel}>404</p>
            <div className={styles.emptyState}>
              <p className={styles.notFoundCode}>404</p>
              <p className={styles.emptyTitle}>Case gone cold.</p>
              <p className={styles.swatchNote}>This page left no evidence.</p>
              <button type="button" className={styles.btnGhost}>
                Back to the scene <span className={styles.arrow} aria-hidden>→</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── Closing ──────────────────────────────────────────────── */}
      <section className={styles.closing}>
        <Seal uid="close" showText={false} className={styles.closingSeal} />
        <h2 className={styles.closingTitle}>Shall we begin?</h2>
        <button type="button" className={styles.btnPrimary}>
          Host your dinner <span className={styles.arrow} aria-hidden>→</span>
        </button>
        <p className={styles.closingFine}>You supply the guests. We supply the guilt.</p>
      </section>
    </div>
  );
};

export default BrandMock;
